import { useState, useEffect } from 'react';
import './share-dialog.css';

interface ShareDialogProps {
  isOpen: boolean;
  fileName: string;
  fileKey: string;
  onClose: () => void;
}

type Permission = 'read' | 'read-write' | 'disabled';

interface ShareLink {
  shareId: string;
  permission: Permission;
  createdAt: string;
  expiresAt?: string;
}

export const ShareDialog = ({ isOpen, fileName, fileKey, onClose }: ShareDialogProps) => {
  const [permission, setPermission] = useState<Permission>('read');
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchShareLinks();
    }
  }, [isOpen, fileKey]);

  const fetchShareLinks = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/share/links/${encodeURIComponent(fileKey)}`);
      if (response.ok) {
        const data = await response.json();
        setShareLinks(data.links || []);
      }
    } catch (err) {
      setError('Failed to load share links');
    } finally {
      setIsLoading(false);
    }
  };

  const generateShareLink = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('/api/share/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileKey,
          permission,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create share link');
      }

      const data = await response.json();
      const url = `${window.location.origin}/share/${data.shareId}`;
      setShareUrl(url);
      setShareLinks([...shareLinks, {
        shareId: data.shareId,
        permission,
        createdAt: new Date().toISOString(),
      }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create share link');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const disableShareLink = async (shareId: string) => {
    try {
      const response = await fetch(`/api/share/disable/${shareId}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to disable share link');
      }

      setShareLinks(shareLinks.filter(link => link.shareId !== shareId));
      if (shareUrl.includes(shareId)) {
        setShareUrl('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable share link');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="share-dialog-overlay" onClick={onClose}>
      <div className="share-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="share-dialog-header">
          <h2>Share: {fileName}</h2>
          <button className="share-dialog-close" onClick={onClose}>×</button>
        </div>

        <div className="share-dialog-content">
          {error && (
            <div className="share-error">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
              <button className="error-close" onClick={() => setError(null)}>×</button>
            </div>
          )}

          <div className="share-section">
            <h3>Generate New Share Link</h3>
            
            <div className="share-permission-group">
              <label>Permission Level:</label>
              <div className="permission-options">
                <label className="permission-radio">
                  <input
                    type="radio"
                    value="read"
                    checked={permission === 'read'}
                    onChange={(e) => setPermission(e.target.value as Permission)}
                  />
                  <span className="permission-label">
                    <span className="permission-icon">👁️</span>
                    Read Only
                  </span>
                  <span className="permission-desc">Anyone can view</span>
                </label>

                <label className="permission-radio">
                  <input
                    type="radio"
                    value="read-write"
                    checked={permission === 'read-write'}
                    onChange={(e) => setPermission(e.target.value as Permission)}
                  />
                  <span className="permission-label">
                    <span className="permission-icon">✏️</span>
                    Read & Write
                  </span>
                  <span className="permission-desc">Anyone can view and download</span>
                </label>
              </div>
            </div>

            <button
              className="share-generate-btn"
              onClick={generateShareLink}
              disabled={isLoading}
            >
              {isLoading ? '⏳ Generating...' : '🔗 Generate Link'}
            </button>
          </div>

          {shareUrl && (
            <div className="share-link-section">
              <h3>Share Link</h3>
              <div className="share-url-container">
                <input
                  type="text"
                  className="share-url-input"
                  value={shareUrl}
                  readOnly
                  onClick={(e) => e.currentTarget.select()}
                />
                <button
                  className="share-copy-btn"
                  onClick={copyToClipboard}
                  title={copied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copied ? '✅' : '📋'}
                </button>
              </div>
              <p className="share-info">
                ℹ️ Anyone with this link can access the file (no login required)
              </p>
            </div>
          )}

          {shareLinks.length > 0 && (
            <div className="share-links-list">
              <h3>Active Share Links ({shareLinks.length})</h3>
              <div className="links-container">
                {shareLinks.map((link) => (
                  <div key={link.shareId} className="share-link-item">
                    <div className="link-info">
                      <span className="link-permission">
                        {link.permission === 'read' && '👁️ Read Only'}
                        {link.permission === 'read-write' && '✏️ Read & Write'}
                      </span>
                      <span className="link-date">
                        Created: {new Date(link.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      className="link-disable-btn"
                      onClick={() => disableShareLink(link.shareId)}
                      title="Disable this share link"
                    >
                      🔒
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="share-tips">
            <h4>💡 Share Link Features</h4>
            <ul>
              <li>Share links don't require login</li>
              <li>Anonymous users can access shared files</li>
              <li>Disable any link at any time</li>
              <li>Each link has separate permissions</li>
            </ul>
          </div>
        </div>

        <div className="share-dialog-footer">
          <button className="close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};
